import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedQuery } from 'flow/hooks/api/useAuthenticatedQuery';

import { networkErrorDispatcher } from 'flow/actions/app-actions';
import AiSettingsResource from 'flow/api/aisettings';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { ExtensionKey } from 'flow/types/EntityKeys';

type AiDigestServiceSettingsDTO = {
    dailySummaryEmail?: boolean;
    weeklySummaryEmail?: boolean;
};

type UpdateAiDigestServiceSettingsArgs = {
    extensionKey: ExtensionKey;
    settings: AiDigestServiceSettingsDTO;
};

const aiDigestServiceSettingsQueryKey = (extensionKey: ExtensionKey) => [
    'aiDigestServiceSettings',
    extensionKey,
];

const getAiDigestServiceSettings = (
    extensionKey: ExtensionKey,
): Promise<AiDigestServiceSettingsDTO> =>
    new Promise((resolve, reject) => {
        AiSettingsResource.getSettings(
            { onResult: resolve, onError: reject },
            extensionKey,
        );
    });

const putAiDigestServiceSettings = ({
    extensionKey,
    settings,
}: UpdateAiDigestServiceSettingsArgs): Promise<void> =>
    new Promise((resolve, reject) => {
        AiSettingsResource.updateSettings(
            { onResult: () => resolve(), onError: reject },
            extensionKey,
            settings,
        );
    });

export const useAiDigestServiceSettings = (extensionKey: ExtensionKey) =>
    useAuthenticatedQuery({
        queryKey: aiDigestServiceSettingsQueryKey(extensionKey),
        queryFn: () => getAiDigestServiceSettings(extensionKey),
    });

type SummaryEmailFrequency = 'daily' | 'weekly';

const useUpdateSummaryEmail = (
    extensionKey: ExtensionKey,
    frequency: SummaryEmailFrequency,
) => {
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();

    const { mutateAsync } = useMutation({
        mutationFn: (enabled: boolean) => {
            const settings =
                frequency === 'daily'
                    ? { dailySummaryEmail: enabled }
                    : { weeklySummaryEmail: enabled };
            return putAiDigestServiceSettings({ extensionKey, settings });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: aiDigestServiceSettingsQueryKey(extensionKey),
            });
        },
        onError: networkErrorDispatcher(dispatch),
    });

    return mutateAsync;
};

const useOptimisticToggle = (
    incoming: boolean | undefined,
    loading: boolean,
    update: (enabled: boolean) => Promise<void>,
) => {
    // Optimistic override; cleared whenever the server value changes so
    // the displayed value falls back to `incoming`.
    const [override, setOverride] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        setOverride(undefined);
    }, [incoming]);

    const value = override ?? incoming ?? false;

    const onChange = (enabled: boolean) => {
        setOverride(enabled);
        update(enabled).catch(() => setOverride(!enabled));
    };

    return { value, loading, onChange };
};

export const useDailySummaryEmailToggle = (extensionKey: ExtensionKey) => {
    const { data: settings, isPending } =
        useAiDigestServiceSettings(extensionKey);
    const update = useUpdateSummaryEmail(extensionKey, 'daily');
    return useOptimisticToggle(settings?.dailySummaryEmail, isPending, update);
};

export const useWeeklySummaryEmailToggle = (extensionKey: ExtensionKey) => {
    const { data: settings, isPending } =
        useAiDigestServiceSettings(extensionKey);
    const update = useUpdateSummaryEmail(extensionKey, 'weekly');
    return useOptimisticToggle(settings?.weeklySummaryEmail, isPending, update);
};
